import { cn, hasMessage } from '@/lib/utils';
import { MutableRefObject, useContext, useEffect, useMemo, useState } from 'react';

import {
  ChainlitContext,
  FileSpec,
  useChatMessages,
  useChatSession,
  useConfig,
} from '@chainlit/react-client';

import { Logo } from '@/components/Logo';
import { Markdown } from '@/components/Markdown';

import MessageComposer from './MessageComposer';
import Starters from './Starters';

interface Props {
  fileSpec: FileSpec;
  onFileUpload: (payload: File[]) => void;
  onFileUploadError: (error: string) => void;
  autoScrollRef: MutableRefObject<boolean>;
}

export default function WelcomeScreen(props: Props) {
  const apiClient = useContext(ChainlitContext);
  const { config } = useConfig();
  const { chatProfile } = useChatSession();
  const { messages } = useChatMessages();
  const [isVisible, setIsVisible] = useState(false);

  const chatProfiles = config?.chatProfiles;

  const allowHtml = config?.features?.unsafe_allow_html;
  const latex = config?.features?.latex;

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const currentChatProfile = useMemo(
    () => chatProfiles?.find((cp) => cp.name === chatProfile),
    [chatProfiles, chatProfile],
  );

  const logo = useMemo(() => {
    if (currentChatProfile?.icon) {
      return (
        <img
          className="w-16 h-16 rounded-full"
          src={
            currentChatProfile.icon.startsWith('/public')
              ? apiClient.buildEndpoint(currentChatProfile.icon)
              : currentChatProfile.icon
          }
          alt=""
        />
      );
    }
    return <Logo className="w-[200px]" />;
  }, [currentChatProfile, apiClient]);

  const markdownDescription = currentChatProfile?.markdown_description;

  if (hasMessage(messages)) return null;

  return (
    <div
      id="welcome-screen"
      className={cn(
        'flex flex-col -mt-[60px] gap-4 w-full flex-grow items-center justify-center welcome-screen mx-auto transition-opacity duration-500 opacity-0 delay-100',
        isVisible && 'opacity-100',
      )}
    >
      <div className="flex flex-col items-center gap-4 mb-2">
        <div className="flex flex-row items-center justify-center gap-4">
          {logo}
          <h1 className="text-5xl font-bold">RECAP</h1>
        </div>
        {markdownDescription ? (
          <div className="w-full max-w-2xl text-center text-muted-foreground">
            <Markdown allowHtml={allowHtml} latex={latex}>
              {markdownDescription}
            </Markdown>
          </div>
        ) : null}
      </div>
      <Starters />
      <MessageComposer {...props} />
    </div>
  );
}
